/**
 * Integrytas Group - Dynamic API Documentation & Sandbox (DAD)
 * Page: DAD.EXPLORER
 *
 * Live API explorer: pick a documented entity, see its reflected schema (real +
 * custom columns), download the runtime OpenAPI spec, and run sandbox calls.
 * Reads execute live (RLS-enforced); writes default to a non-persisting dry-run
 * with an explicit "Execute for real" step. Everything goes through the real
 * gateway, so the active user's role + workspace boundaries always apply.
 */

import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { asfWorkspaceCode } from '../../lib/asf';
import { hasAccess, type AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

interface EntityRow { entity_name: string; title: string | null; allowed_kinds: unknown }
interface ColRow { name: string; type: string; format?: string; required?: boolean; custom?: boolean; db_type?: string; enum?: unknown }
type Op = 'select' | 'insert' | 'update' | 'delete';

const EXAMPLE_BY_TYPE = (c: ColRow): unknown =>
  c.type === 'integer' ? 0 : c.type === 'number' ? 0 : c.type === 'boolean' ? false
    : c.format === 'date' ? '2026-01-01' : c.format === 'date-time' ? '2026-01-01T00:00:00Z' : '';

export interface DadExplorerPageProps { session: AuthSession }

export function DadExplorerPage({ session }: DadExplorerPageProps) {
  return (
    <PersonalizationProvider pageCode="DAD.EXPLORER" session={session}>
      <DadExplorerPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'DAD.EXPLORER';
function DadExplorerPageInner({ session }: DadExplorerPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const [ws, setWs] = useState(asfWorkspaceCode() || '');
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [entity, setEntity] = useState<EntityRow | null>(null);
  const [cols, setCols] = useState<ColRow[]>([]);
  const [op, setOp] = useState<Op>('select');
  const [payload, setPayload] = useState('');
  const [result, setResult] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TODO: wire relations — each schema grid row is a single reflected column property,
  // not a parent of other records; the disclosure renders inert (spacer).
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_name: string): RelatedChildGroup[] => [];
  const hasRelated = (name: string) => buildRelatedGroups(name).some(g => g.items.length > 0);

  useEffect(() => { void (async () => {
    const { data } = await apiDb.from('dad_documented_entity').select('entity_name, title, allowed_kinds').eq('is_active', 'Y').order('sort_order').order('entity_name');
    setEntities((data ?? []) as EntityRow[]);
  })(); }, []);

  const selectEntity = async (e: EntityRow) => {
    setEntity(e); setResult(''); setError(null); setBusy(true);
    try {
      const { data, error: er } = await apiDb.rpc<ColRow[]>('dad_entity_columns', { p_entity: e.entity_name, p_workspace: ws });
      if (er) throw er;
      setCols(Array.isArray(data) ? data : []);
      const kinds = Array.isArray(e.allowed_kinds) ? (e.allowed_kinds as string[]) : ['select'];
      setOp((kinds[0] as Op) ?? 'select');
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  // Build an example request payload whenever entity/op/cols change.
  useEffect(() => {
    if (!entity) { setPayload(''); return; }
    const pk = cols.find(c => c.name.endsWith('_id') && !c.custom)?.name ?? `${entity.entity_name}_id`;
    if (op === 'select') {
      setPayload(JSON.stringify({ columns: '*', filters: [], limit: 20 }, null, 2));
    } else {
      const row: Record<string, unknown> = {};
      const ext: Record<string, unknown> = {};
      for (const c of cols) {
        if (['created_at', 'updated_at', 'modified_by'].includes(c.name)) continue;
        if (op !== 'update' && op !== 'delete' && c.name === pk) continue; // identity on insert
        if (c.custom) ext[c.name.split('.')[1]] = EXAMPLE_BY_TYPE(c);
        else row[c.name] = (op === 'delete' && c.name !== pk) ? undefined : EXAMPLE_BY_TYPE(c);
      }
      if (Object.keys(ext).length) row.ext_attributes = ext;
      const cr: Record<string, unknown> = { table_name: entity.entity_name, operation: op === 'insert' ? 'ADD' : op === 'update' ? 'CHANGE' : 'DELETE', row };
      if (op !== 'insert') cr.key_columns = [pk];
      setPayload(JSON.stringify(cr, null, 2));
    }
    setResult('');
  }, [entity, op, cols]);

  const parse = (): Record<string, unknown> | null => { try { return JSON.parse(payload); } catch { setError(t('dad.error.invalidJson', 'Payload is not valid JSON.')); return null; } };

  const runRead = async () => {
    if (!entity) return; const body = parse(); if (!body) return;
    setBusy(true); setError(null);
    try {
      let q = apiDb.from(entity.entity_name).select((body.columns as string) ?? '*');
      if (typeof body.limit === 'number') q = q.limit(body.limit);
      const { data, error: e } = await q;
      if (e) throw e;
      setResult(JSON.stringify(data ?? [], null, 2));
    } catch (err) { setError((err as Error).message); setResult(''); } finally { setBusy(false); }
  };
  const runDryRun = async () => {
    const body = parse(); if (!body) return;
    setBusy(true); setError(null);
    try { const { data, error: e } = await apiDb.rpc('dad_dry_run', { p_payload: body }); if (e) throw e; setResult(JSON.stringify(data, null, 2)); }
    catch (err) { setError((err as Error).message); setResult(''); } finally { setBusy(false); }
  };
  const runExecute = async () => {
    const body = parse(); if (!body) return;
    if (!confirm(t('dad.confirm.execute', 'Execute for real against live data? This write goes through the gateway (RLS-enforced) and persists.'))) return;
    setBusy(true); setError(null);
    try { const { data, error: e } = await apiDb.rpc('csm_common_routine_with_context', { p_payload: body }); if (e) throw e; setResult(JSON.stringify(data, null, 2)); }
    catch (err) { setError((err as Error).message); setResult(''); } finally { setBusy(false); }
  };

  const downloadOpenApi = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('dad_openapi', { p_workspace: ws });
      if (e) throw e;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `openapi-${ws || 'global'}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const allowed = (entity && Array.isArray(entity.allowed_kinds) ? (entity.allowed_kinds as string[]) : ['select']) as Op[];

  const SCHEMA_COLS: GridColumnDef[] = [
    { key: 'property', header: t('dad.col.property', 'Property'), defaultWidth: 200 },
    { key: 'type',     header: t('dad.col.type', 'Type'),         defaultWidth: 160 },
    { key: 'required', header: t('dad.col.required', 'Required'), defaultWidth: 90  },
    { key: 'source',   header: t('dad.col.source', 'Source'),     defaultWidth: 90  },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('dad.title', 'API Explorer & Sandbox')}</span>
        <input value={ws} onChange={e => setWs(e.target.value)} placeholder={t('dad.placeholder.workspace', 'workspace')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-xs" />
        <button onClick={() => void downloadOpenApi()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('dad.downloadOpenApi', 'Download OpenAPI')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('dad.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <div className="shrink-0 px-3 pt-2">
          <ErrorBanner
            message={t('dad.load_failed', "Couldn't complete the API explorer request.")}
            detail={error}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="w-64 shrink-0 overflow-auto border-r border-csm-border">
          {entities.map(e => (
            <button key={e.entity_name} onClick={() => void selectEntity(e)} className={`flex w-full flex-col items-start border-b border-csm-border-soft px-2 py-1 text-left hover:bg-csm-surface-alt ${entity?.entity_name === e.entity_name ? 'bg-csm-surface-alt' : ''}`}>
              <span className="font-mono text-csm-xs text-csm-ink">{e.entity_name}</span>
              {e.title && e.title !== e.entity_name && <span className="text-csm-2xs text-csm-watermark">{e.title}</span>}
            </button>
          ))}
          {entities.length === 0 && <div className="p-3"><EmptyState variant="no-data" title={t('dad.empty.entities', 'No documented entities.')} /></div>}
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-3">
          {!entity && <div className="text-csm-sm text-csm-watermark">{t('dad.selectEntity', 'Select an entity to see its live schema and run sandbox calls.')}</div>}
          {entity && (
            <>
              <h3 className="mb-1 font-mono text-csm-base text-csm-ink">{entity.entity_name}</h3>
              <PersonalizedGrid componentKey="schema" columns={SCHEMA_COLS}>
                {(gcols, widths) => (
                  <table className="mb-4 w-full max-w-2xl border-collapse text-csm-sm">
                    <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                      <th className="w-7 px-1 py-1" title={t('dad.related.header', 'Related records')} />
                      {gcols.map(c => <th key={c.key} className="px-2 py-1" style={{ width: widths[c.key] }}>{c.header}</th>)}
                    </tr></thead>
                    <tbody>
                      {cols.map(c => (
                        <Fragment key={c.name}>
                        <tr className="border-b border-csm-border-soft">
                          <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton
                              open={detail.isOpen(c.name)}
                              hasChildren={hasRelated(c.name)}
                              onToggle={() => detail.toggle(c.name)}
                              expandLabel={t('dad.related.expand', 'Show related records')}
                              collapseLabel={t('dad.related.collapse', 'Hide related')}
                              tooltip={t('dad.related.tooltip', 'Show related records')}
                            />
                          </td>
                          {gcols.map(gc => {
                            if (gc.key === 'property') return <td key={gc.key} style={{ width: widths[gc.key] }} className="px-2 py-1 font-mono text-csm-xs">{c.name}</td>;
                            if (gc.key === 'type')     return <td key={gc.key} style={{ width: widths[gc.key] }} className="px-2 py-1 text-csm-2xs text-csm-ink-soft">{c.type}{c.format ? ` · ${c.format}` : ''}</td>;
                            if (gc.key === 'required') return <td key={gc.key} style={{ width: widths[gc.key] }} className="px-2 py-1 text-csm-xs">{c.required ? <span className="text-csm-danger">{t('dad.required.yes', 'yes')}</span> : ''}</td>;
                            if (gc.key === 'source')   return <td key={gc.key} style={{ width: widths[gc.key] }} className="px-2 py-1 text-csm-2xs">{c.custom ? <span className="rounded bg-amber-100 px-1 text-amber-800">{t('dad.source.custom', 'custom')}</span> : <span className="text-csm-watermark">{t('dad.source.column', 'column')}</span>}</td>;
                            return null;
                          })}
                        </tr>
                        {detail.isOpen(c.name) && (
                          <tr>
                            <td colSpan={gcols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                              <RelatedChildrenPanel groups={buildRelatedGroups(c.name)} session={session} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>

              <div className="mb-2 flex items-center gap-2">
                <h4 className="text-csm-sm font-semibold text-csm-ink">{t('dad.sandbox', 'Sandbox')}</h4>
                <select value={op} onChange={e => setOp(e.target.value as Op)} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                  {allowed.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                {op === 'select'
                  ? <button onClick={() => void runRead()} className="h-7 rounded border border-primary bg-primary px-3 text-csm-xs text-white hover:opacity-90">{t('dad.runQuery', 'Run query')}</button>
                  : <>
                      <button onClick={() => void runDryRun()} className="h-7 rounded border border-primary bg-primary px-3 text-csm-xs text-white hover:opacity-90">{t('dad.dryRun', 'Dry-run')}</button>
                      {canChange && (<button onClick={() => void runExecute()} className="h-7 rounded border border-csm-danger px-3 text-csm-xs text-csm-danger hover:bg-csm-surface-alt">{t('dad.executeForReal', 'Execute for real')}</button>)}
                    </>}
                <span className="text-csm-2xs text-csm-watermark">{op === 'select' ? t('dad.hint.select', 'live, RLS-enforced') : t('dad.hint.write', 'dry-run validates without writing')}</span>
              </div>
              <textarea value={payload} onChange={e => setPayload(e.target.value)} rows={10} className="w-full rounded border border-csm-border p-2 font-mono text-csm-2xs" spellCheck={false} />
              {result && <pre className="mt-2 max-h-80 overflow-auto rounded border border-csm-border-soft bg-csm-surface-alt p-2 text-csm-2xs text-csm-ink">{result}</pre>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
